import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SealCheck, DownloadSimple, XCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { downloadCertificate } from '@/components/CertificateModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_debt-cycle/artifacts/gzyrfqiy_diametor_logo_white-removebg-preview.png";
const CUR = '\u20B9';

export default function PublicCertificatePage() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/certificate/${id}`)
      .then(res => setCert(res.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="cert-not-found">
        <div className="text-center">
          <XCircle size={48} className="text-zinc-600 mx-auto mb-3" weight="bold" />
          <p className="font-ibm text-zinc-400">Certificate not found or has been revoked.</p>
        </div>
      </div>
    );
  }
  if (!cert) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-10 px-4" data-testid="public-certificate-page">
      <div className="max-w-xl mx-auto bg-white rounded-md overflow-hidden">
        <div className="text-center border-b-4 border-yellow-400 p-6">
          <img src={LOGO} alt="Diametor" className="h-10 mx-auto" />
          <h1 className="font-outfit text-xl font-bold tracking-wide mt-3 text-zinc-900">ELECTRONIC {cert.cert_type === 'payment' ? 'CERTIFICATE OF PAYMENT' : cert.cert_type === 'loan_closure' ? 'CERTIFICATE OF LOAN CLOSURE' : 'CERTIFICATE OF DISCHARGE'}</h1>
          <p className="font-ibm text-xs text-zinc-500 mt-1">Certificate ID: {cert.certificate_id}</p>
          <div className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-sm bg-green-100 text-green-700">
            <SealCheck size={14} weight="bold" /><span className="font-ibm text-[11px] font-semibold">Verified · Issued by Diametor</span>
          </div>
        </div>
        <div className="p-6">
          <p className="font-ibm text-sm text-zinc-700 mb-4">This electronically certifies that the following {cert.cert_type === 'payment' ? 'payables of' : cert.cert_type === 'loan_closure' ? 'bank/NBFC loan of' : 'obligations of'} <strong>{cert.user_name}</strong> {cert.cert_type === 'payment' ? 'were paid to their creditors' : cert.cert_type === 'loan_closure' ? 'was fully paid off' : 'were discharged'} via Diametor's multilateral netting engine.</p>

          {/* Parties */}
          <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 space-y-2 mb-4" data-testid="public-cert-parties">
            <p className="font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Parties</p>
            <div>
              <p className="font-ibm text-[10px] uppercase text-zinc-500">{cert.cert_type === 'payment' ? 'Certificate Holder (Payer)' : 'Certificate Holder (Payee)'}</p>
              <p className="font-ibm text-sm font-semibold text-zinc-900">{cert.user_name}</p>
              {(cert.user_email || cert.user_phone) && <p className="font-ibm text-[11px] text-zinc-600">{[cert.user_email, cert.user_phone].filter(Boolean).join(' · ')}</p>}
            </div>
            {(cert.cleared_debts || []).map((d, i) => (
              <div key={i}>
                <p className="font-ibm text-[10px] uppercase text-zinc-500">{d.role === 'loan' ? 'Loan with' : d.role === 'debtor' ? 'Paid to' : 'Received from'}</p>
                <p className="font-ibm text-sm font-semibold text-zinc-900">{d.counterparty}</p>
                {(d.counterparty_email || d.counterparty_phone) && <p className="font-ibm text-[11px] text-zinc-600">{[d.counterparty_email, d.counterparty_phone].filter(Boolean).join(' · ')}</p>}
              </div>
            ))}
          </div>

          <div className="space-y-1.5 mb-4">
            {(cert.cleared_debts || []).map((d, i) => (
              <div key={i} className="flex justify-between text-sm font-ibm text-zinc-700">
                <span>{d.role === 'loan' ? 'Loan with' : d.role === 'debtor' ? 'Paid to' : 'Received from'} {d.counterparty}</span>
                <span className="font-semibold">{CUR}{(d.amount || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-200 pt-3 space-y-1 mb-4">
            <div className="flex justify-between text-sm font-ibm text-zinc-700"><span>Total Cleared</span><span className="font-bold">{CUR}{(cert.total_cleared || 0).toLocaleString()}</span></div>
            {cert.received_amount > 0 && <div className="flex justify-between text-sm font-ibm text-zinc-700"><span>Received</span><span className="font-bold">{CUR}{cert.received_amount.toLocaleString()}</span></div>}
            <div className="flex justify-between text-sm font-ibm text-zinc-700"><span>Processing Fee ({cert.fee_pct != null ? cert.fee_pct : (cert.cert_type === 'payment' ? 10 : 5)}%)</span><span className="font-semibold">{CUR}{(cert.fee_paid || 0).toLocaleString()}</span></div>
            <div className="flex justify-between text-sm font-ibm text-zinc-500"><span>Issued</span><span>{cert.issued_at ? new Date(cert.issued_at).toLocaleString() : ''}</span></div>
          </div>
          {cert.cert_type === 'loan_closure' && cert.closure && (
            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 space-y-1 mb-4" data-testid="public-closure-details">
              <p className="font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Loan Closure Payment Details</p>
              {[['Amount Paid', `${CUR}${(cert.closure.amount || 0).toLocaleString()}`], ['Reference', cert.closure.reference || '—'], ['Mode', cert.closure.mode || '—'], ['Paid On', `${cert.closure.paid_date || ''} ${cert.closure.paid_time || ''}`.trim() || '—'], ...(cert.closure.particulars ? [['Particulars', cert.closure.particulars]] : [])].map(([k, v], i) => (
                <div key={i} className="flex justify-between text-sm font-ibm text-zinc-700"><span>{k}</span><span className="font-semibold">{v}</span></div>
              ))}
            </div>
          )}
          <p className="font-ibm text-[11px] italic bg-zinc-50 border-l-2 border-yellow-400 p-3 text-zinc-600">{cert.clause}</p>
          <p className="font-ibm text-[11px] text-center text-zinc-600 mt-3" data-testid="public-cert-dispute-note">If there is any dispute, kindly contact <strong>support@diametor.com</strong> or call us at <strong>+91-9072336912</strong>.</p>
          <p className="font-ibm text-[10px] leading-relaxed p-3 rounded-sm border border-orange-200 bg-orange-50 text-orange-800 mt-3" data-testid="public-cert-noc-disclaimer"><strong>Disclaimer:</strong> NOCs or Documents pledged or related to the amounts settled has to be requested and collected personally or directly from the debt provider, creditor, financial institutions or banks and As a Facilitator Platform we "Diametor" or Its Partners or Stake holders are never or no longer responsible or liable for any damage or delay or problems that may arise now or later in the future.</p>
          <button onClick={() => downloadCertificate(cert)} className="w-full h-11 mt-4 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 flex items-center justify-center gap-2" data-testid="public-download-btn">
            <DownloadSimple size={16} weight="bold" /> Download Certificate
          </button>
        </div>
      </div>
    </div>
  );
}
